/**
 * Collect string leaf values from a JSON tree for batch translation.
 * @param {*} obj
 * @param {string} [pathPrefix]
 * @returns {{ path: string, value: string }[]}
 */
export function collectStringLeaves(obj, pathPrefix = '') {
  const entries = [];

  if (obj === null || obj === undefined) return entries;

  if (typeof obj === 'string') {
    entries.push({ path: pathPrefix, value: obj });
    return entries;
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const childPath = pathPrefix ? `${pathPrefix}[${index}]` : `[${index}]`;
      entries.push(...collectStringLeaves(item, childPath));
    });
    return entries;
  }

  if (typeof obj === 'object') {
    Object.keys(obj).forEach(key => {
      const childPath = joinChildPath(pathPrefix, key);
      entries.push(...collectStringLeaves(obj[key], childPath));
    });
  }

  return entries;
}

function escapePathKey(key) {
  return String(key).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Build a path segment for an object key. Keys containing "." must use bracket
 * notation so flat keys like "wild" and "wild.1" do not collide.
 * @param {string} pathPrefix
 * @param {string|number} key
 * @returns {string}
 */
function joinChildPath(pathPrefix, key) {
  const keyStr = String(key);
  const needsQuotes = /[.[\]]/.test(keyStr);

  if (!pathPrefix) {
    return needsQuotes ? `['${escapePathKey(keyStr)}']` : keyStr;
  }

  return needsQuotes ? `${pathPrefix}['${escapePathKey(keyStr)}']` : `${pathPrefix}.${keyStr}`;
}

/**
 * @param {string} path
 * @returns {(string|number)[]}
 */
export function parseLeafPath(path) {
  if (!path) return [];

  const segments = [];
  let i = 0;

  while (i < path.length) {
    if (path[i] === '.') {
      i += 1;
      continue;
    }

    if (path[i] === '[') {
      if (path[i + 1] === "'") {
        i += 2;
        let key = '';
        while (i < path.length) {
          if (path[i] === '\\' && i + 1 < path.length) {
            key += path[i + 1];
            i += 2;
            continue;
          }
          if (path[i] === "'") break;
          key += path[i];
          i += 1;
        }
        if (path[i] === "'") i += 1;
        if (path[i] === ']') i += 1;
        segments.push(key);
        continue;
      }

      const match = path.slice(i).match(/^\[(\d+)\]/);
      if (match) {
        segments.push(Number(match[1]));
        i += match[0].length;
        continue;
      }
    }

    const match = path.slice(i).match(/^[^.[\]]+/);
    if (!match) break;
    segments.push(match[0]);
    i += match[0].length;
  }

  return segments;
}

function isContainer(value) {
  return value !== null && typeof value === 'object';
}

const DANGEROUS_PATH_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

function isSafePathSegment(seg) {
  if (typeof seg === 'number') return Number.isInteger(seg) && seg >= 0;
  if (typeof seg !== 'string' || !seg) return false;
  return !DANGEROUS_PATH_KEYS.has(seg);
}

function setByPath(root, path, value) {
  if (!path) return;

  const segments = parseLeafPath(path);
  if (!segments.length || !segments.every(isSafePathSegment)) return;

  let current = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i];
    const next = segments[i + 1];

    if (!isContainer(current)) return;

    if (typeof seg === 'number') {
      if (!Array.isArray(current)) return;
      if (current[seg] === undefined) {
        current[seg] = typeof next === 'number' ? [] : {};
      }
      current = current[seg];
      continue;
    }

    if (current[seg] === undefined) {
      current[seg] = typeof next === 'number' ? [] : {};
    }
    current = current[seg];
  }

  const last = segments[segments.length - 1];
  if (!isContainer(current)) return;

  if (typeof last === 'number') {
    if (Array.isArray(current)) current[last] = value;
    return;
  }

  current[last] = value;
}

/**
 * @param {*} obj
 * @param {string} path
 * @returns {*}
 */
export function getValueAtPath(obj, path) {
  if (!path) return obj;

  const segments = parseLeafPath(path);
  if (!segments.length || !segments.every(isSafePathSegment)) return undefined;

  let current = obj;
  for (const seg of segments) {
    if (current == null || typeof current !== 'object') return undefined;
    if (typeof seg === 'number') {
      if (!Array.isArray(current)) return undefined;
      current = current[seg];
      continue;
    }
    current = current[seg];
  }
  return current;
}

/**
 * Normalize key-path picker values (primitives or `{ value|path }` objects).
 * Strips autocomplete labels like `path — preview`.
 * @param {unknown} raw
 * @param {number} [max=20]
 * @returns {string[]}
 */
export function normalizeSelectedKeyPaths(raw, max = 20) {
  const list = Array.isArray(raw) ? raw : [];
  const out = [];
  const seen = new Set();

  for (const item of list) {
    let path = '';
    if (item && typeof item === 'object') {
      path = String(item.value || item.path || '').trim();
    } else {
      path = String(item || '').trim();
    }
    if (!path) continue;

    // v-autocomplete may store item-text (`path — preview`) instead of item-value
    const labelSep = ' — ';
    const sepIdx = path.indexOf(labelSep);
    if (sepIdx > 0) {
      path = path.slice(0, sepIdx).trim();
    }

    if (seen.has(path)) continue;
    seen.add(path);
    out.push(path);
    if (out.length >= max) break;
  }

  return out;
}

/**
 * @param {{ path: string, value: string }[]} entries
 * @param {string[]} paths
 * @returns {{ path: string, value: string }[]}
 */
export function filterEntriesByPaths(entries, paths) {
  const wanted = new Set(normalizeSelectedKeyPaths(paths, Number.MAX_SAFE_INTEGER));
  if (!wanted.size) return [];
  return (entries || []).filter(entry => wanted.has(entry.path));
}

function cloneStructure(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => cloneStructure(item));
  const copy = {};
  Object.keys(obj).forEach(key => {
    copy[key] = cloneStructure(obj[key]);
  });
  return copy;
}

/**
 * Build a deep copy of obj with translated string leaves.
 * @param {*} obj
 * @param {{ path: string, value: string }[]} entries
 * @param {string[]} translations
 * @returns {*}
 */
export function applyTranslations(obj, entries, translations) {
  const result = cloneStructure(obj);
  entries.forEach((entry, index) => {
    setByPath(result, entry.path, translations[index] ?? entry.value);
  });
  return result;
}

/**
 * Merge translated values into an existing locale object (other keys unchanged).
 * Creates intermediate objects/arrays for missing paths.
 * @param {*} localeObj
 * @param {{ path: string, value: string }[]} entries
 * @param {string[]} translations
 * @returns {*}
 */
export function mergeTranslations(localeObj, entries, translations) {
  const base =
    localeObj !== null && typeof localeObj === 'object' && !Array.isArray(localeObj)
      ? localeObj
      : {};
  const result = cloneStructure(base);
  entries.forEach((entry, index) => {
    setByPath(result, entry.path, translations[index] ?? entry.value);
  });
  return result;
}

export { setByPath };

